import type {
  CopyPermissionScheme,
  CreatePermissionScheme,
  EffectivePermissions,
  Page,
  PermissionScheme,
  PermissionSchemeDetail,
  ProjectPermissionScheme,
  PutGrants,
  SwitchPermissionScheme,
  UpdatePermissionScheme,
} from '@servicetim/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { adminPath, useApiContext } from './client';
import { DEFAULT_PAGE_SIZE, pageQuery, usePagedList } from './paging';

const base = adminPath('/permission-schemes');

/** `GET /admin/v1/permission-schemes` (with `projectCount`). */
export function usePermissionSchemes(pageSize = DEFAULT_PAGE_SIZE) {
  const { request, keys } = useApiContext();
  return usePagedList<PermissionScheme>({
    queryKey: keys.permissionSchemes.list({ pageSize }),
    fetchPage: (cursor, signal) =>
      request<Page<PermissionScheme>>('GET', base, { query: pageQuery(cursor, pageSize), signal }),
  });
}

/** `GET /admin/v1/permission-schemes/{sid}`: scheme + full grant matrix. */
export function usePermissionScheme(schemeId: string | undefined) {
  const { request, keys } = useApiContext();
  return useQuery({
    queryKey: keys.permissionSchemes.detail(schemeId ?? ''),
    queryFn: ({ signal }) =>
      request<PermissionSchemeDetail>('GET', `${base}/${schemeId}`, { signal }),
    enabled: schemeId !== undefined,
  });
}

export function useCreatePermissionScheme() {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: (input: CreatePermissionScheme) =>
      request<PermissionSchemeDetail>('POST', base, { body: input }),
    onSuccess: () => invalidate(keys.permissionSchemes.all),
  });
}

export function useUpdatePermissionScheme() {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdatePermissionScheme }) =>
      request<PermissionSchemeDetail>('PATCH', `${base}/${id}`, { body: patch }),
    onSuccess: () => invalidate(keys.permissionSchemes.all),
  });
}

export function useDeletePermissionScheme() {
  const { request, keys, invalidate, remove } = useApiContext();
  return useMutation({
    mutationFn: (id: string) => request<unknown>('DELETE', `${base}/${id}`),
    onSuccess: (_r, id) => {
      remove(keys.permissionSchemes.detail(id));
      return invalidate(keys.permissionSchemes.all);
    },
  });
}

/** `POST /admin/v1/permission-schemes/{sid}/copy` */
export function useCopyPermissionScheme() {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: ({ id, ...input }: CopyPermissionScheme & { id: string }) =>
      request<PermissionSchemeDetail>('POST', `${base}/${id}/copy`, { body: input }),
    onSuccess: () => invalidate(keys.permissionSchemes.all),
  });
}

/** `PUT /admin/v1/permission-schemes/{sid}/grants`: the complete matrix, replaced atomically. */
export function usePutPermissionGrants() {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: ({ id, grants }: { id: string; grants: PutGrants }) =>
      request<PermissionSchemeDetail>('PUT', `${base}/${id}/grants`, { body: grants }),
    onSuccess: (_s, { id }) =>
      invalidate(keys.permissionSchemes.detail(id), keys.projects.all, keys.tickets.all),
  });
}

/** `POST /admin/v1/projects/{id}/permission-scheme`: switch the project to another scheme. */
export function useSwitchProjectPermissionScheme(projectId: string) {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: (input: SwitchPermissionScheme) =>
      request<ProjectPermissionScheme>(
        'POST',
        adminPath(`/projects/${projectId}/permission-scheme`),
        {
          body: input,
        },
      ),
    onSuccess: () =>
      invalidate(
        keys.projects.detail(projectId),
        keys.project(projectId).scope,
        keys.permissionSchemes.all,
      ),
  });
}

/** `GET /admin/v1/projects/{id}/effective-permissions?userId=` (defaults to the caller). */
export function useEffectivePermissions(projectId: string | undefined, userId?: string) {
  const { request, keys } = useApiContext();
  return useQuery({
    queryKey: keys.project(projectId ?? '').effectivePermissions(userId),
    queryFn: ({ signal }) =>
      request<EffectivePermissions>(
        'GET',
        adminPath(`/projects/${projectId}/effective-permissions`),
        {
          query: { userId },
          signal,
        },
      ),
    enabled: projectId !== undefined,
  });
}
